
/**
 * Contexto para sincronização global do Pipeline
 * 
 * O que faz:
 * - Gerencia estado global de sincronização do pipeline
 * - Fornece funções para notificar mudanças de etapa
 * - Controla invalidação inteligente do cache
 * 
 * Onde é usado:
 * - App.tsx como provider principal
 * - LeadInfoSidebar para notificar mudanças
 * - PipelineBoard para escutar atualizações
 * 
 * Como funciona:
 * - LeadInfoSidebar atualiza etapa → notifica contexto
 * - PipelineBoard escuta notificação → atualiza em tempo real
 * - Cache é invalidado de forma inteligente
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

interface PipelineNotification {
  leadId: string;
  fromEtapaId: string | null;
  toEtapaId: string;
  timestamp: number;
}

interface PipelineContextData {
  // Estado de notificações
  lastNotification: PipelineNotification | null;
  
  // Funções para notificar mudanças
  notifyLeadStageChange: (leadId: string, fromEtapaId: string | null, toEtapaId: string) => void;
  
  // Função para forçar atualização do pipeline
  forceRefreshPipeline: () => void;
  
  // Estado de loading para sincronização
  isSyncing: boolean;
}

const PipelineContext = createContext<PipelineContextData | undefined>(undefined);

interface PipelineProviderProps {
  children: React.ReactNode;
}

/**
 * Provider do contexto Pipeline
 * Gerencia sincronização global entre componentes
 */
export const PipelineProvider = ({ children }: PipelineProviderProps) => {
  const [lastNotification, setLastNotification] = useState<PipelineNotification | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const queryClient = useQueryClient();

  /**
   * Notifica mudança de etapa de um lead
   * Usado pela LeadInfoSidebar após atualizar etapa
   */
  const notifyLeadStageChange = useCallback((
    leadId: string,
    fromEtapaId: string | null,
    toEtapaId: string
  ) => {
    console.log('🔄 [PipelineContext] Notificando mudança de etapa:', {
      leadId,
      fromEtapaId,
      toEtapaId
    });

    const notification: PipelineNotification = {
      leadId,
      fromEtapaId,
      toEtapaId,
      timestamp: Date.now()
    };

    setLastNotification(notification);
    setIsSyncing(true);

    // Invalida somente as colunas de origem/destino já abertas; uma infinite query
    // global poderia refazer todas as páginas em sequência.
    queryClient.getQueryCache().findAll({ queryKey: ['pipeline-leads-coluna'] }).forEach((consulta) => {
      const etapaDaQuery = consulta.queryKey[2];
      if (etapaDaQuery === fromEtapaId || etapaDaQuery === toEtapaId) {
        queryClient.invalidateQueries({ queryKey: consulta.queryKey });
      }
    });
    queryClient.getQueryCache().findAll({ queryKey: ['pipeline-leads-contagens-agrupadas'] }).forEach((consulta) => {
      const etapasDaQuery = Array.isArray(consulta.queryKey[2]) ? consulta.queryKey[2].map(String) : [];
      if (etapasDaQuery.includes(String(fromEtapaId)) || etapasDaQuery.includes(toEtapaId)) {
        queryClient.invalidateQueries({ queryKey: consulta.queryKey });
      }
    });
    queryClient.invalidateQueries({ queryKey: ['leads'], refetchType: 'none' });
    queryClient.invalidateQueries({ queryKey: ['adminPipelineData'], refetchType: 'none' });
    setIsSyncing(false);
  }, [queryClient]);

  /**
   * Força atualização completa do pipeline
   * Usado em casos de sincronização manual
   */
  const forceRefreshPipeline = useCallback(() => {
    console.log('🔄 [PipelineContext] Forçando atualização completa do pipeline');
    
    setIsSyncing(true);
    
    // A atualização manual pode refazer apenas as queries ativas do novo fluxo.
    queryClient.invalidateQueries({ queryKey: ['pipeline-leads-coluna'], refetchType: 'active' });
    queryClient.invalidateQueries({ queryKey: ['pipeline-leads-contagens-agrupadas'], refetchType: 'active' });
    queryClient.invalidateQueries({ queryKey: ['leads'], refetchType: 'none' });
    queryClient.invalidateQueries({ queryKey: ['adminPipelineData'], refetchType: 'none' });
    queryClient.invalidateQueries({ queryKey: ['etapas'], refetchType: 'active' });
    setIsSyncing(false);
  }, [queryClient]);

  const contextValue: PipelineContextData = {
    lastNotification,
    notifyLeadStageChange,
    forceRefreshPipeline,
    isSyncing
  };

  return (
    <PipelineContext.Provider value={contextValue}>
      {children}
    </PipelineContext.Provider>
  );
};

/**
 * Hook para usar o contexto Pipeline
 * Throws error se usado fora do PipelineProvider
 */
export const usePipelineContext = () => {
  const context = useContext(PipelineContext);
  
  if (context === undefined) {
    throw new Error('usePipelineContext deve ser usado dentro de um PipelineProvider');
  }
  
  return context;
};
